import React from 'react';
import { BookOpen } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Lesson } from '../../../types';
import { getDomains, getGrades, getSubjects } from '../../../utils/lessons';

interface LessonMetadataSectionProps {
  formData: Partial<Lesson>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Lesson>>>;
  errors: Record<string, string>;
}

export const LessonMetadataSection: React.FC<LessonMetadataSectionProps> = ({
  formData,
  setFormData,
  errors
}) => {
  const { t } = useTranslation();

  const subjects = getSubjects(t);
  
  const grades = getGrades(t);

  const domains = getDomains(t);
  
  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <BookOpen className="w-5 h-5 mr-2" />
        {t('lesson.lessonInformation')} *
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.title')} *
          </label>
          <input
            type="text"
            value={formData.metadata?.title || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, title: e.target.value }
            }))}
            className={`input-field ${errors.title ? 'border-red-300' : ''}`}
            placeholder={t('lesson.enterLessonTitle')}
          />
          {errors.title && <p className="text-red-600 text-sm mt-1">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.subject')} *
          </label>
          <select
            value={formData.metadata?.subject || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, subject: e.target.value }
            }))}
            className={`input-field ${errors.subject ? 'border-red-300' : ''}`}
          >
            <option value="">{t('lesson.selectSubject')}</option>
            {subjects.map(subject => (
              <option key={subject.value} value={subject.value}>{subject.label}</option>
            ))}
          </select>
          {errors.subject && <p className="text-red-600 text-sm mt-1">{errors.subject}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.className')}
          </label>
          <input
            type="text"
            value={formData.className || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              className: e.target.value
            }))}
            className="input-field"
            placeholder={t('lesson.enterClassName')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.domain')}
          </label>
          <select
            value={formData.metadata?.domain || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, domain: e.target.value }
            }))}
            className={`input-field ${errors.domain ? 'border-red-300' : ''}`}
          >
            <option value="">{t('lesson.selectDomain')}</option>
            {domains.map(domain => (
              <option key={domain.value} value={domain.value}>{domain.label}</option>
            ))}
          </select>
          {errors.domain && <p className="text-red-600 text-sm mt-1">{errors.domain}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.construct')}
          </label>
          <input
            type="text"
            value={formData.metadata?.construct || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, construct: e.target.value }
            }))}
            className={`input-field ${errors.construct ? 'border-red-300' : ''}`}
            placeholder={t('lesson.enterConstruct')}
          />
          {errors.construct && <p className="text-red-600 text-sm mt-1">{errors.construct}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.subconstruct')}
          </label>
          <input
            type="text"
            value={formData.metadata?.subconstruct || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, subconstruct: e.target.value }
            }))}
            className={`input-field ${errors.subconstruct ? 'border-red-300' : ''}`}
            placeholder={t('lesson.enterSubconstruct')}
          />
          {errors.subconstruct && <p className="text-red-600 text-sm mt-1">{errors.subconstruct}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.grade')} *
          </label>
          <select
            value={formData.metadata?.grade || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, grade: e.target.value }
            }))}
            className={`input-field ${errors.grade ? 'border-red-300' : ''}`}
          >
            <option value="">{t('lesson.selectGrade')}</option>
            {grades.map(grade => (
              <option key={grade.value} value={grade.value}>{grade.label}</option>
            ))}
          </select>
          {errors.grade && <p className="text-red-600 text-sm mt-1">{errors.grade}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.duration')} (minutes) *
          </label>
          <input
            type="number"
            value={formData.metadata?.duration_minutes || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              metadata: { ...prev.metadata!, duration_minutes: parseInt(e.target.value) || 0 }
            }))}
            className={`input-field ${errors.duration ? 'border-red-300' : ''}`}
            placeholder="30"
            min="1"
          />
          {errors.duration && <p className="text-red-600 text-sm mt-1">{errors.duration}</p>}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.globalMinimumProficiency')}
          </label>
          <textarea
            value={formData.metadata?.global_minimum_proficiency || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, metadata: { ...prev.metadata!, global_minimum_proficiency: e.target.value } }))}
            className="input-field"
            rows={3}
            placeholder={t('lesson.globalMinimumProficiencyPlaceholder')}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('lesson.prerequisites')}
          </label>
          <textarea
            value={formData.prerequisites || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, prerequisites: e.target.value }))}
            className="input-field"
            rows={3}
            placeholder={t('lesson.prerequisitesPlaceholder')}
          />
        </div>
      </div>
    </div>
  );
};
